import styles from './AdditionalMealExpenseEditOverlay.module.scss';

import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLock } from '@work4all/components/lib/hooks';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { TravelAdditionalMealExpenses } from '@work4all/models/lib/Classes/TravelAdditionalMealExpenses.entity';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';
import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { BusinessPartnerPickerField } from '../../../../../../components/entity-picker/BusinessPartnerPickerField';
import { ProjectPickerField } from '../../../../../../components/entity-picker/ProjectPickerField';
import { NavigationOverlayHeader } from '../../../../../../components/navigation-overlay-header/NavigationOverlayHeader';
import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { Panel } from '../../../components';
import { useConfirmBeforeCloseMask } from '../../../hooks/use-confrm-before-close-mask';
import { TravelExpensesFormValue } from '../type';

type TravelAdditionalMealExpensesValue = PathsOf<
  TravelAdditionalMealExpenses,
  2
>;

export interface AdditionalMealExpenseEditOverlayProps {
  expense: TravelAdditionalMealExpenses;
  onVerify: (data: TravelExpensesFormValue) => void;
  update: (
    id: number,
    key: string,
    value: number | string | boolean,
    updater?: (
      data: TravelAdditionalMealExpenses
    ) => TravelAdditionalMealExpenses
  ) => TravelExpensesFormValue;
}

export const AdditionalMealExpenseEditOverlay: React.FC<
  AdditionalMealExpenseEditOverlayProps
> = ({ expense, onVerify, update }) => {
  const { t } = useTranslation();
  const { locked } = useLock();

  const form = useForm<TravelAdditionalMealExpensesValue>({
    defaultValues: { ...expense },
  });

  const { watch, register, control } = form;

  useEffect(() => {
    form.reset(expense);
  }, [expense, form]);

  const watchedValues = watch();

  useConfirmBeforeCloseMask(form.formState.isDirty, undefined, true);

  return (
    <FormProvider {...form}>
      <NavigationOverlayHeader
        title={
          watchedValues.dateTime
            ? DateTime.fromISO(watchedValues.dateTime).toLocaleString(
                DateTimeCustom.DATE_SIMPLE_2YEAR
              )
            : ''
        }
        breadcrumbsChildren={
          <Button type="submit" title={t('INPUTS.SAVE')} disabled={locked} />
        }
      />
      <Panel>
        <div className={styles.container}>
          <LabeledInput
            label={t('COMMON.HOURS')}
            {...register('hours')}
            type="number"
            disabled
          />

          <LabeledInput
            label={t('TRAVEL.AMOUNT')}
            {...register('amount')}
            type="number"
            disabled
          />

          <div>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t('TRAVEL.DEDUCTIONS')}
            </Typography>

            <div className={styles.deductionItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={locked}
                    checked={watchedValues.deductionBreakfast || false}
                    onChange={(e) => {
                      const data = update(
                        expense.id,
                        'deductionBreakfast',
                        e.target.checked
                      );
                      onVerify(data);
                    }}
                  />
                }
                label={t('TRAVEL.BREAKFAST_DEDUCTION')}
              />

              {watchedValues.deductionBreakfast && (
                <ControllerPlus
                  control={control}
                  name={'deductionBreakfastAmount'}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        onChange={(e) => {
                          const exitValue = e as number;
                          const newValue =
                            exitValue < 0 ? exitValue : -exitValue;
                          update(
                            expense.id,
                            'deductionBreakfastAmount',
                            newValue
                          );
                        }}
                        step={0.01}
                        decimals={2}
                        hideControls
                      />
                    );
                  }}
                />
              )}
            </div>

            <div className={styles.deductionItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={locked}
                    checked={watchedValues.deductionLunch || false}
                    onChange={(e) => {
                      const data = update(
                        expense.id,
                        'deductionLunch',
                        e.target.checked
                      );
                      onVerify(data);
                    }}
                  />
                }
                label={t('TRAVEL.LUNCH_DEDUCTION')}
              />
              {watchedValues.deductionLunch && (
                <ControllerPlus
                  control={control}
                  name={'deductionLunchAmount'}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        onChange={(e) => {
                          const exitValue = e as number;
                          const newValue =
                            exitValue < 0 ? exitValue : -exitValue;
                          update(expense.id, 'deductionLunchAmount', newValue);
                        }}
                        step={0.01}
                        decimals={2}
                        hideControls
                      />
                    );
                  }}
                />
              )}
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  disabled={locked}
                  checked={watchedValues.deductionDinner || false}
                  onChange={(e) => {
                    const data = update(
                      expense.id,
                      'deductionDinner',
                      e.target.checked
                    );
                    onVerify(data);
                  }}
                />
              }
              label={t('TRAVEL.DINNER_DEDUCTION')}
            />
            {watchedValues.deductionDinner && (
              <ControllerPlus
                control={control}
                name={'deductionDinnerAmount'}
                render={({ field }) => {
                  return (
                    <NumberInput
                      {...field}
                      onChange={(e) => {
                        const exitValue = e as number;
                        const newValue = exitValue < 0 ? exitValue : -exitValue;
                        update(expense.id, 'deductionDinnerAmount', newValue);
                      }}
                      step={0.01}
                      decimals={2}
                      hideControls
                    />
                  );
                }}
              />
            )}
          </div>

          <ControllerPlus
            control={control}
            name="project"
            render={({ field }) => (
              <ProjectPickerField
                {...field}
                label={t('COMMON.PROJECT')}
                disabled={locked}
                clearable
                onChange={(project) => {
                  const data = update(
                    expense.id,
                    'projectId',
                    project?.id,
                    (data) => {
                      return {
                        ...data,
                        project: project,
                        projectId: project?.id,
                      };
                    }
                  );
                  onVerify(data);
                }}
              />
            )}
          />

          <ControllerPlus
            control={control}
            name="company"
            render={({ field }) => (
              <BusinessPartnerPickerField
                {...field}
                label={t('TRAVEL.CUSTOMER_SUPPLIER')}
                disabled={locked}
                value={
                  field.value?.__typename === 'Geschaeftspartner'
                    ? field.value?.data
                    : field.value?.data ?? field.value
                }
                onChange={(businessPartner) => {
                  const bp = businessPartner?.data;
                  const data = update(
                    expense.id,
                    'businessPartnerId',
                    bp?.id,
                    (data) => {
                      return {
                        ...data,
                        businessPartnerId: bp?.id,
                        businessPartnerType:
                          bp?.__typename === 'Supplier'
                            ? SdObjType.LIEFERANT
                            : SdObjType.KUNDE,
                        company: bp,
                      };
                    }
                  );
                  onVerify(data);
                }}
              />
            )}
          />

          <ControllerPlus
            control={control}
            name="note"
            render={({ field }) => (
              <LabeledInput
                {...field}
                onBlur={(e) => {
                  update(expense.id, 'note', e.target.value);
                }}
                label={t('INPUTS.NOTE/REMARK')}
                disabled={locked}
                multiline
                minRows={5}
              />
            )}
          />
        </div>
      </Panel>
    </FormProvider>
  );
};
